import { colours } from "@shared/ui.design-system";
import { CloseIcon, TickIcon } from "@shared/ui.icons";
import { MarkdownContainer, Typography } from "@shared/ui.primitives";
import React from "react";

import {
  conIcon,
  container,
  header,
  itemRow,
  proIcon,
  section
} from "./ProsAndConsList.css";

import "@shared/ui.primitives/style.css";

export interface ProsAndConsListProps {
  heading: string;
  pros?: string[];
  cons?: string[];
}

/**
 * Renders a list of pros and cons with a heading.
 *
 * Displays each pro with a tick icon and each con with a close icon.
 * The heading and list items support Markdown formatting.
 *
 * @param {ProsAndConsListProps} props - The props for the ProsAndConsList component.
 * @param {string} props.heading - The heading text for the list, rendered as Markdown.
 * @param {string[]} [props.pros] - An optional array of pros to display.
 * @param {string[]} [props.cons] - An optional array of cons to display.
 *
 * @returns {JSX.Element} The rendered ProsAndConsList component.
 */
export const ProsAndConsList: React.FC<ProsAndConsListProps> = ({
  heading,
  pros = [],
  cons = []
}) => {
  return (
    <div className={container} data-testid="pros-cons-list">
      <div className={header}>
        <MarkdownContainer
          content={heading}
          tagOverrides={{
            span: {
              component: Typography,
              props: {
                as: "h6",
                variant: "subheader-6",
                colour: "primary-dark"
              }
            }
          }}
        />
      </div>

      {pros.length ? (
        <div className={section} aria-label="Beauty pie membership pros list">
          {pros.map((pro, idx) => (
            <div className={itemRow} key={`pro-${idx}`}>
              <span className={proIcon}>
                <TickIcon
                  fill={colours.secondary.light}
                  width={16}
                  height={16}
                />
              </span>
              <MarkdownContainer
                content={pro}
                forceBlock
                tagOverrides={{
                  p: {
                    component: Typography,
                    props: {
                      as: "p",
                      variant: "body-sm",
                      colour: "primary-dark"
                    }
                  },
                  span: {
                    component: Typography,
                    props: {
                      as: "span",
                      variant: "body-sm",
                      colour: "primary-dark"
                    }
                  }
                }}
              />
            </div>
          ))}
        </div>
      ) : null}
      {cons.length ? (
        <div className={section} aria-label="Beauty pie membership cons list">
          {cons.map((con, idx) => (
            <div className={itemRow} key={`con-${idx}`}>
              <span className={conIcon}>
                <CloseIcon
                  fill={colours.secondary.light}
                  width={16}
                  height={16}
                />
              </span>
              <MarkdownContainer
                content={con}
                forceBlock
                tagOverrides={{
                  p: {
                    component: Typography,
                    props: {
                      as: "p",
                      variant: "body-sm",
                      colour: "primary-dark"
                    }
                  },
                  span: {
                    component: Typography,
                    props: {
                      as: "span",
                      variant: "body-sm",
                      colour: "primary-dark"
                    }
                  }
                }}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
